import { Component, OnInit, ViewChild, inject } from '@angular/core';

import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, NgForm, Validators, ReactiveFormsModule } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { CategoryUserGroups, Categories, UserGroupPermissions, UserGroups, Permissions } from '@parashift/shared/constants';
import { SessionService } from '@parashift/shared/services';
import { IterateObjectPipe } from '@parashift/shared/pipes';

@Component({
  selector: 'invite-users-modal',
  templateUrl: './invite-users-modal.component.html',
  styleUrls: ['./invite-users-modal.component.scss'],
  imports: [ReactiveFormsModule, IterateObjectPipe]
})
export class InviteUsersModalComponent implements OnInit {
  bsModalRef = inject(BsModalRef);
  private formBuilder = inject(UntypedFormBuilder);
  private sessionService = inject(SessionService);

  @ViewChild('inviteForm') set iForm(iForm: NgForm) {
     if (iForm) {
        this.inviteForm = iForm;
     }
  }

  private readonly categoryUserGroups = CategoryUserGroups;
  private readonly userGroupPermissions = UserGroupPermissions;
  readonly categories = Categories;
  readonly userGroups = UserGroups;
  readonly permissions = Permissions;

  formCategoryUserGroups = {};
  formUserGroupPermissions = {};

  globalAccess = false;
  isTenantOwner = false;
  isTenantAdmin = false;

  inviteForm: NgForm;
  state: boolean;
  showForm = false;
  form: UntypedFormGroup;
  fullPermission = false;
  isAdmin = false;
  isOwner = false;
  disableSubmit = true;

  get email() { return this.form.get('email'); }

  ngOnInit() {
    setTimeout(() => {
      this.buildForm();
      this.checkGlobalAccess();
      this.initCurrentUsersPermissions();
      this.initGroupsAndPermissions();
      this.showForm = true;
    });
  }

  private checkGlobalAccess() {
    if (this.sessionService.userIsAdminSupportOrSales) {
      this.globalAccess = true;
    }
  }

  private initCurrentUsersPermissions() {
    this.isTenantOwner = this.bsModalRef.content.data.editorPermissions.indexOf('owner') !== -1;
    this.isTenantAdmin = this.bsModalRef.content.data.editorPermissions.indexOf('admin') !== -1;
  }

  onCheckboxChange(event: EventTarget) {
    const value = (event as HTMLInputElement).value;
    const checked = (event as HTMLInputElement).checked;

    if (value === 'admin' || value === 'owner') {
      this.toggleAdminPermissions(checked, value);
    } else if (checked) {
      this.addUserGroup(value);
    } else {
      this.removeUsergroup(value);
    }

    this.checkuserGroups();
  }

  submitForm() {
    this.form.get('email').markAsTouched();

    if (this.form.valid) {
      let permissions = [];
      const usergroups = [];

      this.form.value.userGroups.forEach((userGroup: string) => {
        if (userGroup.includes('pdc_group.')) {
          usergroups.push(userGroup);
          if (this.formUserGroupPermissions[userGroup]) {
            permissions = [...permissions, ...this.formUserGroupPermissions[userGroup]];
          }
        } else {
          permissions.push(userGroup);
        }
      });

      permissions = permissions.reduce((unique: string[], permission: string) => {
        return unique.includes(permission) ? unique : [...unique, permission];
      }, []);

      this.bsModalRef.content.model.permissions = permissions;
      this.bsModalRef.content.model.user_groups = usergroups;
      this.bsModalRef.content.model.email = this.form.value.email;
      this.state = true;
      this.bsModalRef.hide();
    }
  }

  close() {
    this.bsModalRef.content.state = false;
    this.bsModalRef.hide();
  }

  private buildForm() {
    this.form = this.formBuilder.group({
      email: new UntypedFormControl('', [
        Validators.required,
        Validators.email
      ]),
      userGroups: this.formBuilder.array([])
    });
  }

  private initGroupsAndPermissions() {
    const allowedPermissions = this.setUpAllowedPermissionsAndGroups(this.bsModalRef.content.model.tenant_permissions || []);

    if (allowedPermissions.some(p => p.includes('pdc.'))) {
      for (const category of Object.keys(this.categoryUserGroups)) {
        this.formCategoryUserGroups[category] = [];
        this.categoryUserGroups[category].forEach(userGroup => {
          this.formCategoryUserGroups[category].push(userGroup);

          if (userGroup !== 'admin' && userGroup !== 'owner') {
            this.formUserGroupPermissions[userGroup] = [];
            this.userGroupPermissions[userGroup].forEach(permission => {
              if (allowedPermissions.includes(permission)) {
                this.formUserGroupPermissions[userGroup].push(permission);
              }
            });

            if (this.formUserGroupPermissions[userGroup].length === 0) {
              delete this.formUserGroupPermissions[userGroup];
            }
          }
        });

        if (this.formCategoryUserGroups[category].length === 0) {
          delete this.formCategoryUserGroups[category];
        }
      }
    }

    this.checkuserGroups();
  }

  private setUpAllowedPermissionsAndGroups(allowedPermissions) {
    const userGroups = [];

    for (const userGroup of Object.keys(this.userGroups)) {
      if (userGroup.includes('pdc_group.')) {
        userGroups.push(userGroup);
      }
    }

    return allowedPermissions = [...allowedPermissions, ...userGroups];
  }

  private toggleAdminPermissions(checked: boolean, value: string) {
    const userGroups: UntypedFormArray = this.form.get('userGroups') as UntypedFormArray;
    this.isAdmin = (checked && value === 'admin') || (this.form.value.userGroups.includes('admin') && value !== 'admin');
    this.isOwner = (checked && value === 'owner') || (this.form.value.userGroups.includes('owner') && value !== 'owner');

    if (checked) {
      userGroups.push(new UntypedFormControl(value));

      for (let i = userGroups.controls.length - 1; i >= 0; i--) {
        if (userGroups.controls[i].value !== 'admin' && userGroups.controls[i].value !== 'owner') {
          userGroups.removeAt(i);
        }
      }
      this.fullPermission = true;
    } else {
      this.removeUsergroup(value);

      if (!this.form.value.userGroups.includes('admin') && !this.form.value.userGroups.includes('owner')) {
        this.fullPermission = false;
      }
    }
  }

  private addUserGroup(value: string) {
    const userGroups: UntypedFormArray = this.form.get('userGroups') as UntypedFormArray;
    userGroups.push(new UntypedFormControl(value));
  }

  private removeUsergroup(value: string) {
    const userGroups: UntypedFormArray = this.form.get('userGroups') as UntypedFormArray;
    const index = userGroups.controls.findIndex(control => control.value === value);

    if (index > -1) {
      userGroups.removeAt(index);
    }
  }

  private checkuserGroups() {
    const userGroups = this.form.get('userGroups');

    if (userGroups['length'] > 0) {
      this.disableSubmit = false;
    } else {
      this.disableSubmit = true;
    }
  }
}
